import Link from 'next/link'

import type { FC, ReactNode } from 'react'
import type { PostCard as PostCardType } from '@/types'

import { cn } from '@/lib/utils'
import { PillarChip } from '@/components/content/pillar-chip'
import { PostMeta } from '@/components/content/post-meta'

interface PostCardProps {
    post: PostCardType
    className?: string
}

export const PostCard: FC<PostCardProps> = ({ post, className }): ReactNode => {
    const href = `/${post.pillarSlug}/${post.slug}`

    return (
        <article className={cn('group flex flex-col', className)}>
            <Link
                href={href}
                tabIndex={-1}
                aria-hidden='true'
                className='relative mb-5 block aspect-[16/11] overflow-hidden rounded-xl border border-border/60 bg-muted'
            >
                {post.coverImage && (
                    <img
                        src={post.coverImage}
                        alt=''
                        loading='lazy'
                        className='absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]'
                    />
                )}
            </Link>

            <PillarChip
                accent={post.accent}
                label={post.pillarName}
                href={`/${post.pillarSlug}`}
                cmsRef={`pillars:${post.pillarId}:name`}
                className='self-start'
            />

            <h3 className='mt-3 font-heading text-xl font-semibold leading-snug tracking-tight text-foreground'>
                <Link
                    href={href}
                    data-mdlfy-cms={`posts:${post.id}:title`}
                    className='transition-colors hover:text-primary'
                >
                    {post.title}
                </Link>
            </h3>

            <p
                data-mdlfy-cms={`posts:${post.id}:excerpt`}
                className='mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground'
            >
                {post.excerpt}
            </p>

            <PostMeta
                className='mt-4'
                publishedAt={post.publishedAt}
                readMinutes={post.readMinutes}
            />
        </article>
    )
}
